/* Margin Finder · qualquer agente, inclusive um que este código nunca leu.
 *
 * As quatro seções curadas sabem o que cada campo QUER DIZER: telecom sabe que o
 * penhasco pós-promoção é o número que a vitrine esconde, seguros sabe que ramo
 * indeterminado é motivo de desconfiança. Esta tela não sabe nada disso — ela desenha
 * pela FORMA do valor, não pelo nome: escalar vira fato, lista de texto vira passos,
 * lista de objetos vira tabela e cartões.
 *
 * A troca é explícita e vale a pena enquanto ninguém leu o schema: mostrar o que
 * existe, com o mesmo peso, é muito melhor que não mostrar. Quando alguém ler, escreve
 * a seção curada e esta some para aquele agente (ver `MarginFinder.tsx`).
 *
 * O `<details>` de JSON cru no fim não é preguiça: é a garantia de que nenhum campo do
 * agente fica inalcançável quando uma das heurísticas daqui erra. */

import { Card, ChartFrame } from "../../components/ui";
import type { GenericoUser, MarginGenericoPayload } from "../../lib/api";
import { brl2, fmtDateTime, fmtMes, intBR, pct } from "../../lib/format";
import { rotuloCampo, tituloBloco, valorEnum } from "../../lib/humano";
import { RelatorioEmTexto } from "./Relatorio";
import {
  BlocoAuditoria, Facts, Hero, Passos, Provenancia, SecaoMargin, economiaLabel,
  host, money,
} from "./comum";

/* ── o que já tem dono ─────────────────────────────────────────────────────
 * Os seis campos que os quatro schemas têm em comum, mais os dois de passos. Saem do
 * caminho antes do baldeamento porque já viram herói, ressalvas ou checklist. */

const CONSUMIDAS = new Set([
  "versao_schema", "status", "resumo", "economia_total",
  "premissas", "auditoria", "antes_de_contratar", "como_obter",
]);

/** Os cinco campos que `BlocoAuditoria` desenha por conta própria. */
const AUDITORIA_PROPRIAS = new Set([
  "limitacoes", "blocos_recebidos", "blocos_ausentes", "ids_citados",
  "regras_de_piso_violadas",
]);

/* ── formato ───────────────────────────────────────────────────────────────
 * Toda heurística de valor mora aqui, num lugar só, porque toda ela pode errar. */

const eEscalar = (v: unknown): v is string | number | boolean =>
  typeof v === "string" || typeof v === "number" || typeof v === "boolean";
const eObjeto = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null && !Array.isArray(v);
const eListaDeTexto = (v: unknown): v is string[] =>
  Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === "string");
const eListaDeObjetos = (v: unknown): v is Record<string, unknown>[] =>
  Array.isArray(v) && v.length > 0 && v.every(eObjeto);

/* Acima disto o valor e prosa, nao dado: vai para paragrafo em vez de celula de
 * `<Facts>`, que e uma coluna estreita de grade. Medido no `agent_custo_financeiro`,
 * cujos `o_que_fazer`/`o_que_conferir` passam de 200 caracteres e saiam espremidos
 * numa coluna de ~150 px. */
const PROSA = 90;
const eProsa = (v: unknown): v is string => typeof v === "string" && v.length > PROSA;

const DINHEIRO = /(valor|custo|preco|preço|economia|premio|prêmio|teto|mensalidade|tarifa|juros|receita|saldo)/;
const PERCENTUAL = /(percentual|_pct$|^pct_|aliquota|alíquota)/;
const DATA_HORA = /^\d{4}-\d{2}-\d{2}[T ]/;
const MES = /^\d{4}-\d{2}$/;
const LINK = /^https?:\/\//i;

/** O valor de um campo, em texto de tela. `null` vira travessão — nunca "R$ 0,00",
 *  que afirmaria "não há o que economizar" onde a verdade é "não deu para saber". */
export function texto(chave: string, v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "sim" : "não";
  if (typeof v === "number") {
    if (PERCENTUAL.test(chave) || chave.endsWith("_pct")) {
      // O agente pode publicar 0,14 ou 14 para os mesmos 14%. Nenhum dos dois é
      // errado, e não há como saber qual é — a fração é o palpite mais comum.
      return v <= 1 && v >= -1 ? pct(v) : `${intBR(v)}%`;
    }
    if (chave.endsWith("_brl") || DINHEIRO.test(chave)) return money(v);
    return Number.isInteger(v) ? intBR(v) : brl2(v);
  }
  if (typeof v === "string") {
    if (LINK.test(v)) return host(v);
    if (DATA_HORA.test(v)) return fmtDateTime(v);
    if (MES.test(v)) return fmtMes(v, true);
    return valorEnum(v);
  }
  // Não deveria chegar aqui: `separa` só manda escalar para cá. Mas o JSON vem de um
  // agente que ninguém leu, e um `String(v)` feio é melhor que um crash na tela.
  return String(v);
}

/** Os escalares de um objeto, no formato que `<Facts>` espera. */
function fatosDe(obj: Record<string, unknown>, pular: Set<string> = new Set()) {
  return Object.entries(obj)
    .filter(([k, v]) => !pular.has(k) && (v === null || eEscalar(v)))
    .map(([k, v]) => ({ k: rotuloCampo(k), v: texto(k, v) }));
}

/* ── baldeamento ─────────────────────────────────────────────────────────── */

interface Baldes {
  fatos: [string, unknown][];
  listas: [string, string[]][];
  tabelas: [string, Record<string, unknown>[]][];
  objetos: [string, Record<string, unknown>][];
  restos: [string, unknown][];
}

/** Separa o JSON por FORMA. A ordem do agente é preservada dentro de cada balde: ele
 *  costuma pôr o que importa primeiro, e é a única pista de prioridade que existe. */
function separa(a: Record<string, unknown>, pular: Set<string>): Baldes {
  const b: Baldes = { fatos: [], listas: [], tabelas: [], objetos: [], restos: [] };
  for (const [k, v] of Object.entries(a)) {
    if (pular.has(k)) continue;
    // Bloco ausente some. Um travessão solto parece bug de front, e a ausência de um
    // bloco inteiro não é um fato a exibir — já `null` ESCALAR vira "—", porque aí é
    // um campo que o agente declarou e não soube.
    if (Array.isArray(v) && v.length === 0) continue;
    if (v === null || eEscalar(v)) b.fatos.push([k, v]);
    else if (eListaDeTexto(v)) b.listas.push([k, v]);
    else if (eListaDeObjetos(v)) b.tabelas.push([k, v]);
    else if (eObjeto(v)) b.objetos.push([k, v]);
    else b.restos.push([k, v]);
  }
  return b;
}

/* ── blocos ────────────────────────────────────────────────────────────────── */

const TITULO = ["nome", "plano", "titulo", "título", "servico", "serviço", "item",
                "produto", "descritor", "descricao", "descrição", "categoria",
                "rotulo", "label", "ramo", "acao", "ação", "tipo"];
const NOTA = ["por_que", "porque", "motivo", "observacao", "observação", "justificativa"];
const ATENCAO = ["atencao", "atenção", "alerta", "risco"];
const FONTE = ["fonte_url", "url", "link", "fonte"];

const primeiroTexto = (o: Record<string, unknown>, chaves: string[]) => {
  for (const k of chaves) if (typeof o[k] === "string" && o[k]) return k;
  return undefined;
};

const MAX_COLUNAS = 8;

/* Cartoes mostrados abertos por bloco; o resto vai para um <details>. Medido no
 * `agent_profile`, que traz 46 objetos em 8 blocos e rendia uma pagina de 21.000 px --
 * rolagem que ninguem percorre e onde nada se acha. Nao e um corte: o que sobra fica a
 * um clique, na tabela do ChartFrame e no JSON cru. */
const CARTOES_ABERTOS = 6;

/** Um item de uma lista de objetos. Três nomes de campo são tratados por nome — a
 *  única concessão da tela, e barata: se o agente não os tiver, nada acontece, e o
 *  CSS deles (`.mf-nota`, `.mf-atencao`, `.mf-source`) já existe. */
function Item({ o, papel, prosa }: {
  o: Record<string, unknown>;
  papel: string;
  /* Decidido pelo BLOCO, nao por valor: se `o_que_fazer` e prosa em um item, ele e
   * prosa em todos. Por valor, o mesmo campo virava paragrafo num cartao e celula de
   * grade no vizinho -- os itens de um bloco deixavam de se parecer entre si. */
  prosa: Set<string>;
}) {
  const kTitulo = primeiroTexto(o, TITULO);
  const kNota = primeiroTexto(o, NOTA);
  const kAtencao = primeiroTexto(o, ATENCAO);
  const kFonte = FONTE.find((k) => typeof o[k] === "string" && LINK.test(o[k] as string));
  const tratadas = new Set([kTitulo, kNota, kAtencao, kFonte].filter(Boolean) as string[]);

  const dentro = separa(o, tratadas);
  const curtos = dentro.fatos.filter(([k]) => !prosa.has(k));
  const longos = dentro.fatos.filter(([k]) => prosa.has(k));

  return (
    <article className="mf-plan">
      {kTitulo && <div className="mf-plan-papel">{papel}</div>}
      <h3>{kTitulo ? valorEnum(o[kTitulo] as string) : papel}</h3>
      {curtos.length > 0 && (
        <Facts items={curtos.map(([k, v]) => ({ k: rotuloCampo(k), v: texto(k, v) }))} />
      )}
      {longos.map(([k, v]) => (
        <p className="mf-nota" key={k}>
          <strong>{rotuloCampo(k)}:</strong> {v as string}
        </p>
      ))}
      {dentro.listas.map(([k, itens]) => (
        <div className="mf-plan-cobertura" key={k}>
          {rotuloCampo(k)}: {itens.join(", ")}
        </div>
      ))}
      {kNota && <p className="mf-nota">{valorEnum(o[kNota] as string)}</p>}
      {kAtencao && <p className="mf-atencao">{valorEnum(o[kAtencao] as string)}</p>}
      {kFonte && (
        <a className="mf-source" href={o[kFonte] as string} target="_blank" rel="noreferrer">
          fonte: {host(o[kFonte] as string)}
        </a>
      )}
    </article>
  );
}

/** Uma lista de objetos: tabela derivada por união de chaves + os cartões. */
function Bloco({ chave, itens }: { chave: string; itens: Record<string, unknown>[] }) {
  const todas = [...new Set(itens.flatMap((i) => Object.keys(i)))]
    .filter((k) => itens.some((i) => i[k] === null || eEscalar(i[k])));
  const prosa = new Set(todas.filter((k) => itens.some((i) => eProsa(i[k]))));
  // Prosa nao vira coluna: uma celula com 200 caracteres arrebenta a tabela, e o
  // texto inteiro ja esta no cartao.
  const colunas = todas.filter((k) => !prosa.has(k)).slice(0, MAX_COLUNAS);
  const cortadas = todas.filter((k) => !prosa.has(k)).length - colunas.length;

  return (
    <ChartFrame
      title={tituloBloco(chave)}
      sub={`${itens.length} item(ns)${
        cortadas > 0 ? ` · a tabela mostra ${colunas.length} de ${todas.length} colunas;`
                     + ` as outras ${cortadas} estão nos cartões e no JSON` : ""}`}
      tableHeader={colunas.map(rotuloCampo)}
      /* Strings já formatadas, nunca números: `ChartFrame` passa célula numérica por
         `cell()`, que arredonda R$ 902,50 para "903" e transforma um contador 0 em
         travessão. As curadas convivem com isso porque sabem o que é dinheiro e o que
         é contagem; aqui não dá para saber. */
      tableRows={itens.map((i) => colunas.map((k) => texto(k, i[k])))}
    >
      <div className="mf-plans">
        {itens.slice(0, CARTOES_ABERTOS).map((o, i) => (
          <Item key={i} o={o} papel={`#${i + 1}`} prosa={prosa} />
        ))}
      </div>
      {itens.length > CARTOES_ABERTOS && (
        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: "pointer", fontSize: 12.5, color: "var(--ink-3)" }}>
            ver os outros {itens.length - CARTOES_ABERTOS}
          </summary>
          <div className="mf-plans" style={{ marginTop: 12 }}>
            {itens.slice(CARTOES_ABERTOS).map((o, i) => (
              <Item key={i} o={o} papel={`#${i + 1 + CARTOES_ABERTOS}`} prosa={prosa} />
            ))}
          </div>
        </details>
      )}
    </ChartFrame>
  );
}

/* ── a sessão ────────────────────────────────────────────────────────────── */

export function Generico({ agentId }: { agentId: string }) {
  return (
    <SecaoMargin<GenericoUser>
      chave={`generico/${agentId}`}
      vazio="Este agente não tem análise para o usuário escolhido."
      optionLabel={economiaLabel}
      notaIgnoradas={
        <>Algumas linhas deste agente não puderam ser lidas como JSON e foram
        descartadas. Como este agente não tem formato conhecido, não dá para dizer se
        era payload de outro agente ou gravação corrompida.</>
      }
      acima={(data) => {
        const payload = data as MarginGenericoPayload;
        return payload.formas > 1 ? (
          <div className="banner" role="status">
            <span aria-hidden>⚠</span>
            <span>
              As linhas deste agente não têm todas o mesmo formato
              ({payload.formas} formatos distintos). Ou o schema mudou no meio da base,
              ou uma rodada gravou o payload de outro agente sob o mesmo{" "}
              <code>{agentId}</code>.
            </span>
          </div>
        ) : null;
      }}
    >
      {(user) => <GenericoDetalhe user={user} agentId={agentId} />}
    </SecaoMargin>
  );
}

function GenericoDetalhe({ user, agentId }: { user: GenericoUser; agentId: string }) {
  const a = user.analise;

  // O agente só publica markdown (`agent_profile` hoje). Dizer isso é melhor que uma
  // tela de travessões, e o relatório logo abaixo é o conteúdo real dele.
  if (a === null) {
    return (
      <div className="mf-page">
        <Card title="Sem análise estruturada">
          <p className="mf-nota">
            Este agente publica só o relatório em texto — não há JSON com números para
            o Margin Finder desenhar. O relatório está abaixo, e também na aba
            Relatórios.
          </p>
        </Card>
        <RelatorioEmTexto userId={user.user_id} agentId={agentId} />
        <Provenancia user={user} agentId={agentId} conferencia={false} />
      </div>
    );
  }

  const b = separa(a, CONSUMIDAS);
  const auditoria = eObjeto(a.auditoria) ? a.auditoria : null;
  const premissas = eListaDeTexto(a.premissas) ? a.premissas : [];
  const passos = (k: string) => (eListaDeTexto(a[k]) ? (a[k] as string[]) : []);
  const textos = (v: unknown) => (eListaDeTexto(v) ? v : []);

  return (
    <div className="mf-page">
      {/* 1 · a decisão */}
      <Card>
        <Hero
          estado={user.estado_economia}
          titulo="economia estimada"
          valor={user.economia_anual_brl === null
            ? "sem economia apurada"
            : `R$ ${brl2(user.economia_anual_brl)}`}
          detalhe={user.economia_anual_brl === null
            ? "O agente não publicou uma economia total para este usuário."
            : <>por ano · {money(user.economia_mensal_brl)}/mês · como o agente
               publicou em <code>economia_total</code>, presumida anual</>}
          faixa
        />
        {(user.status || user.resumo) && (
          <p className="mf-frase">
            {user.status && (
              <span className="tag" style={{ marginRight: 8 }}>{valorEnum(user.status)}</span>
            )}
            {user.resumo}
          </p>
        )}
      </Card>

      {/* 2 · a evidência: tudo que é número ou palavra solta */}
      {b.fatos.length > 0 && (
        <Card title="O que o agente apurou" sub="os campos diretos desta análise">
          <Facts items={b.fatos.map(([k, v]) => ({ k: rotuloCampo(k), v: texto(k, v) }))} />
        </Card>
      )}

      {/* 3 · as listas de itens */}
      {b.tabelas.map(([k, itens]) => <Bloco key={k} chave={k} itens={itens} />)}

      {/* 4 · os objetos aninhados. Profundidade 2 de propósito: mais que isso vira
              visualizador de árvore, que é pior que uma tela. O resto está no JSON. */}
      {b.objetos.length > 0 && (
        <Card title="Detalhes" sub="blocos aninhados desta análise">
          <div className="mf-cols">
            {b.objetos.map(([k, o]) => (
              <div className="mf-subcard" key={k}>
                <div className="mf-subcard-k">{rotuloCampo(k)}</div>
                <Facts items={fatosDe(o)} />
                {Object.entries(o)
                  .filter(([, v]) => eListaDeTexto(v))
                  .map(([sub, v]) => (
                    <p className="mf-nota" key={sub}>
                      {rotuloCampo(sub)}: {(v as string[]).join(", ")}
                    </p>
                  ))}
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* 5 · a ação */}
      <Passos title="Como obter" items={passos("como_obter")} />
      <Passos title="Antes de contratar" items={passos("antes_de_contratar")} />
      {b.listas.map(([k, itens]) => (
        <Card title={tituloBloco(k)} key={k}>
          <ul className="mf-why">{itens.map((x, i) => <li key={i}>{x}</li>)}</ul>
        </Card>
      ))}

      {/* 6 · as ressalvas */}
      {(premissas.length > 0 || auditoria) && (
        <Card title="Premissas e ressalvas" sub="o que o agente assumiu para chegar aqui">
          {premissas.length > 0 && (
            <ul className="mf-why">{premissas.map((p, i) => <li key={i}>{p}</li>)}</ul>
          )}
          {auditoria && (
            <BlocoAuditoria
              limitacoes={textos(auditoria.limitacoes)}
              blocosRecebidos={textos(auditoria.blocos_recebidos)}
              blocosAusentes={textos(auditoria.blocos_ausentes)}
              idsCitados={Array.isArray(auditoria.ids_citados)
                ? (auditoria.ids_citados as (string | number)[]) : []}
              regrasVioladas={textos(auditoria.regras_de_piso_violadas)}
            >
              <Facts items={fatosDe(auditoria, AUDITORIA_PROPRIAS)} />
            </BlocoAuditoria>
          )}
        </Card>
      )}

      {/* 7 · a escotilha: nada do agente fica inalcançável */}
      <details className="mf-relatorio">
        <summary>
          ver o JSON cru desta análise
          {b.restos.length > 0 && ` (${b.restos.length} campo(s) não desenhado(s))`}
        </summary>
        <pre style={{ overflowX: "auto", fontSize: 12, lineHeight: 1.5 }}>
          {JSON.stringify(a, null, 2)}
        </pre>
      </details>

      {/* 8 · o texto do agente e a procedência */}
      <RelatorioEmTexto userId={user.user_id} agentId={agentId} />
      <Provenancia user={user} agentId={agentId} conferencia={false} />
    </div>
  );
}
